import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOwnerWhoami, getOwnerStats, getOwnerUsers } from '../lib/api';
import MobileNav from '../components/MobileNav';
import { motion } from 'framer-motion';
import {
  Activity, RefreshCw, Users, Building2, CreditCard, Home as HomeIcon,
  ScanLine, Sparkles, TrendingUp, Loader2, PoundSterling, Timer, ArrowLeft, Clock,
  Globe, Percent, Search, CalendarDays
} from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, CartesianGrid } from 'recharts';

const PLAN_LABELS = { starter: 'Starter', growth: 'Growth', professional: 'Professional', business: 'Business', enterprise: 'Enterprise' };
const PLAN_COLORS = {
  starter: 'bg-slate-100 text-slate-700', growth: 'bg-blue-100 text-blue-700',
  professional: 'bg-emerald-100 text-emerald-700', business: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700'
};
const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700', trialing: 'bg-blue-100 text-blue-700',
  past_due: 'bg-amber-100 text-amber-700', canceled: 'bg-red-100 text-red-600',
  incomplete: 'bg-slate-100 text-slate-600', expired: 'bg-red-100 text-red-600'
};

const KpiCard = ({ icon: Icon, label, value, sub, gradient, testId }) => (
  <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm" data-testid={testId}>
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${gradient}`}>
      <Icon size={18} className="text-white" />
    </div>
    <p className="text-2xl font-extrabold text-slate-900 leading-none">{value}</p>
    <p className="text-xs font-semibold text-slate-500 mt-1.5">{label}</p>
    {sub != null && <p className="text-[11px] font-medium text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const Pill = ({ label, count, cls }) => (
  <div className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold ${cls || 'bg-slate-100 text-slate-700'}`}>
    <span className="capitalize">{label}</span>
    <span className="font-extrabold">{count}</span>
  </div>
);

const TrendChart = ({ title, data, color, testId }) => {
  const total = (data || []).reduce((a, b) => a + (b.count || 0), 0);
  return (
    <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-slate-700">{title}</p>
        <span className="text-xs font-semibold text-slate-400">{total} in 30d</span>
      </div>
      <div style={{ width: '100%', height: 90 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 300, height: 90 }}>
          <AreaChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id={`grad-${testId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="#F1F5F9" />
            <XAxis dataKey="date" hide />
            <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }}
              labelFormatter={(d) => new Date(d).toLocaleDateString()} formatter={(v) => [v, 'count']} />
            <Area type="monotone" dataKey="count" stroke={color} strokeWidth={2} fill={`url(#grad-${testId})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default function OwnerDashboardPage() {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);

  const loadStats = useCallback(async (range) => {
    setLoading(true);
    try {
      const res = await getOwnerStats(range || {});
      setStats(res.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  const loadUsers = useCallback(async (q) => {
    setUsersLoading(true);
    try {
      const res = await getOwnerUsers({ q: q || '', limit: 100 });
      setUsers(res.data?.users || []);
    } catch (e) { /* silent */ }
    finally { setUsersLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getOwnerWhoami();
        if (res.data?.is_owner) {
          await loadStats();
          await loadUsers();
          setChecked(true);
        } else {
          navigate('/settings', { replace: true });
        }
      } catch (e) {
        navigate('/settings', { replace: true });
      }
    })();
  }, [loadStats, loadUsers, navigate]);

  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
        <Loader2 size={28} className="animate-spin text-white/70" />
      </div>
    );
  }

  const u = stats?.users || {};
  const c = stats?.companies || {};
  const p = stats?.properties || {};
  const co = stats?.checkouts || {};
  const cc = stats?.cleaning_companies || {};
  const trends = stats?.trends || {};

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
      <MobileNav />
      <main className="flex-1 mobile-container pb-28" data-testid="owner-dashboard-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/settings')} data-testid="owner-back-button"
              className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
                <Activity size={22} className="text-emerald-400" /> Owner Dashboard
              </h1>
              <p className="text-xs text-slate-400 font-medium">Live platform stats · you only</p>
            </div>
          </div>
          <button onClick={loadStats} disabled={loading} data-testid="owner-refresh-button"
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          </button>
        </motion.div>

        {!stats ? (
          <div className="py-16 flex items-center justify-center text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            {/* Date range filter */}
            <div className="rounded-2xl p-4 bg-white/5 border border-white/10 flex flex-wrap items-end gap-3" data-testid="owner-date-filter">
              <div className="flex items-center gap-2 text-slate-300 text-sm font-bold mr-2"><CalendarDays size={16} /> Filter by date</div>
              <div>
                <label className="block text-[11px] text-slate-400 mb-1">From</label>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="owner-from-date"
                  className="rounded-lg bg-white/10 text-white text-sm px-2 py-1.5 border border-white/10" />
              </div>
              <div>
                <label className="block text-[11px] text-slate-400 mb-1">To</label>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="owner-to-date"
                  className="rounded-lg bg-white/10 text-white text-sm px-2 py-1.5 border border-white/10" />
              </div>
              <button onClick={() => loadStats({ from_date: fromDate, to_date: toDate })} data-testid="owner-apply-range"
                className="px-4 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors">Apply</button>
              {(fromDate || toDate) && (
                <button onClick={() => { setFromDate(''); setToDate(''); loadStats({}); }} data-testid="owner-clear-range"
                  className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-slate-300 text-sm transition-colors">Clear</button>
              )}
            </div>

            {stats.range && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="owner-range-summary">
                <KpiCard icon={Users} label="Signups (range)" value={stats.range.signups ?? 0} sub={`${stats.range.from || '…'} → ${stats.range.to || '…'}`} gradient="bg-gradient-to-br from-blue-500 to-blue-600" testId="range-signups" />
                <KpiCard icon={Building2} label="Companies (range)" value={stats.range.companies ?? 0} gradient="bg-gradient-to-br from-indigo-500 to-indigo-600" testId="range-companies" />
                <KpiCard icon={ScanLine} label="Checkouts (range)" value={stats.range.checkouts ?? 0} gradient="bg-gradient-to-br from-fuchsia-500 to-fuchsia-600" testId="range-checkouts" />
                <KpiCard icon={CreditCard} label="New paying (range)" value={stats.range.new_paying ?? 0} gradient="bg-gradient-to-br from-emerald-500 to-emerald-600" testId="range-paying" />
              </div>
            )}

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard icon={Users} label="Total users" value={u.total ?? 0} sub={`+${u.new_7d ?? 0} last 7d`} gradient="bg-gradient-to-br from-blue-500 to-blue-600" testId="kpi-total-users" />
              <KpiCard icon={Building2} label="Companies" value={c.total ?? 0} sub={`+${c.new_7d ?? 0} last 7d`} gradient="bg-gradient-to-br from-indigo-500 to-indigo-600" testId="kpi-total-companies" />
              <KpiCard icon={CreditCard} label="Paying" value={c.paying ?? 0} sub={`${c.active_trials ?? 0} active trials`} gradient="bg-gradient-to-br from-emerald-500 to-emerald-600" testId="kpi-paying" />
              <KpiCard icon={PoundSterling} label="Est. MRR" value={`£${(c.mrr ?? 0).toFixed(2)}`} sub="active subs" gradient="bg-gradient-to-br from-amber-500 to-amber-600" testId="kpi-mrr" />
              <KpiCard icon={Percent} label="Trial→Paid" value={`${stats.conversion?.trial_to_paid_pct ?? 0}%`} sub={`${stats.conversion?.paying ?? 0}/${stats.conversion?.total_companies ?? 0} companies`} gradient="bg-gradient-to-br from-green-500 to-green-600" testId="kpi-conversion" />
              <KpiCard icon={HomeIcon} label="Properties" value={p.total ?? 0} sub={`${p.active ?? 0} active`} gradient="bg-gradient-to-br from-teal-500 to-teal-600" testId="kpi-properties" />
              <KpiCard icon={ScanLine} label="Checkouts" value={co.total ?? 0} sub={`+${co.last_7d ?? 0} last 7d`} gradient="bg-gradient-to-br from-fuchsia-500 to-fuchsia-600" testId="kpi-checkouts" />
              <KpiCard icon={Sparkles} label="Cleaning cos." value={cc.total ?? 0} sub={`${cc.active ?? 0} active`} gradient="bg-gradient-to-br from-purple-500 to-purple-600" testId="kpi-cleaning-companies" />
              <KpiCard icon={Timer} label="Cleanings done" value={co.completed_cleanings ?? 0} sub={`${stats.pending_invites ?? 0} pending invites`} gradient="bg-gradient-to-br from-rose-500 to-rose-600" testId="kpi-cleanings-done" />
            </div>

            {/* Breakdowns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm" data-testid="breakdown-users">
                <p className="text-sm font-bold text-slate-700 mb-3">Users by role</p>
                <div className="space-y-2">
                  <Pill label="Admins" count={u.admins ?? 0} cls="bg-blue-50 text-blue-700" />
                  <Pill label="Internal staff" count={u.internal_staff ?? 0} cls="bg-slate-100 text-slate-700" />
                  <Pill label="Company managers" count={u.company_managers ?? 0} cls="bg-indigo-50 text-indigo-700" />
                  <Pill label="Cleaners" count={u.cleaners ?? 0} cls="bg-purple-50 text-purple-700" />
                </div>
              </div>
              <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm" data-testid="breakdown-plans">
                <p className="text-sm font-bold text-slate-700 mb-3">Companies by plan</p>
                <div className="space-y-2">
                  {Object.entries(c.by_plan || {}).map(([plan, n]) => (
                    <Pill key={plan} label={PLAN_LABELS[plan] || plan} count={n} cls={PLAN_COLORS[plan]} />
                  ))}
                </div>
              </div>
              <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm" data-testid="breakdown-status">
                <p className="text-sm font-bold text-slate-700 mb-3">Subscription status</p>
                <div className="space-y-2">
                  {Object.entries(c.by_status || {}).map(([st, n]) => (
                    <Pill key={st} label={st.replace('_', ' ')} count={n} cls={STATUS_COLORS[st]} />
                  ))}
                  <Pill label="Expired trials" count={c.expired_trials ?? 0} cls="bg-red-50 text-red-600" />
                </div>
              </div>
            </div>

            {/* Trends */}
            <div className="flex items-center gap-2 text-slate-300 text-sm font-bold pt-1">
              <TrendingUp size={16} /> Last 30 days
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <TrendChart title="New signups" data={trends.signups} color="#3B82F6" testId="trend-signups" />
              <TrendChart title="New companies" data={trends.companies} color="#6366F1" testId="trend-companies" />
              <TrendChart title="Checkouts" data={trends.checkouts} color="#10B981" testId="trend-checkouts" />
            </div>

            {/* Geography + users */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm" data-testid="breakdown-countries">
                <p className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5"><Globe size={15} /> Users by country</p>
                <div className="space-y-2">
                  {(stats.countries || []).slice(0, 8).map((row) => (
                    <Pill key={row.country} label={row.country} count={row.count} cls="bg-teal-50 text-teal-700" />
                  ))}
                  {(!stats.countries || stats.countries.length === 0) && <p className="text-xs text-slate-400">No data yet</p>}
                </div>
                <p className="text-[11px] text-slate-400 mt-3">Country is detected from now on (existing accounts show "Unknown").</p>
              </div>

              <div className="lg:col-span-2 rounded-2xl p-4 bg-white border border-slate-100 shadow-sm" data-testid="owner-users-table">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <p className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Users size={15} /> Recent users</p>
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={userQuery} onChange={(e) => setUserQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') loadUsers(userQuery); }}
                      placeholder="Search name or email" data-testid="owner-users-search"
                      className="pl-8 pr-3 py-1.5 rounded-lg bg-slate-100 text-sm text-slate-700 w-56 max-w-full" />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-100">
                        <th className="py-2 pr-3 font-semibold">Name</th>
                        <th className="py-2 pr-3 font-semibold">Email</th>
                        <th className="py-2 pr-3 font-semibold">Role</th>
                        <th className="py-2 pr-3 font-semibold">Company</th>
                        <th className="py-2 pr-3 font-semibold">Plan</th>
                        <th className="py-2 pr-3 font-semibold">Country</th>
                        <th className="py-2 pr-3 font-semibold">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersLoading ? (
                        <tr><td colSpan={7} className="py-6 text-center text-slate-400"><Loader2 size={16} className="animate-spin inline" /></td></tr>
                      ) : users.length === 0 ? (
                        <tr><td colSpan={7} className="py-6 text-center text-slate-400">No users</td></tr>
                      ) : users.map((row, i) => (
                        <tr key={i} className="border-b border-slate-50" data-testid={`owner-user-row-${i}`}>
                          <td className="py-2 pr-3 font-medium text-slate-800 whitespace-nowrap">{row.name || '—'}</td>
                          <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{row.email}</td>
                          <td className="py-2 pr-3"><span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600 capitalize">{row.role}</span></td>
                          <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{row.company || '—'}</td>
                          <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${PLAN_COLORS[row.plan] || 'bg-slate-100 text-slate-600'}`}>{PLAN_LABELS[row.plan] || row.plan || '—'}</span></td>
                          <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{row.country}</td>
                          <td className="py-2 pr-3 text-slate-500 whitespace-nowrap">{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 flex items-center gap-1.5 pt-1">
              <Clock size={12} /> Updated {stats.generated_at ? new Date(stats.generated_at).toLocaleString() : ''}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
